<script>
import { mapActions, mapState } from 'vuex';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  StarFilled,
  CalendarOutlined,
  UserOutlined,
  FolderOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons-vue';
import { removeObserveTarget } from 'ant-design-vue/es/affix/utils';
import pageUrls from '@/router/pageUrls';
import dateFormat from '@/utils/dateFormat';
import bem from '@/utils/bem';

const b = bem('articles-page');
const { articleEditorPage } = pageUrls;

export default {
  components: {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    SearchOutlined,
    StarFilled,
    CalendarOutlined,
    UserOutlined,
    FolderOutlined,
    SortAscendingOutlined,
    SortDescendingOutlined,
  },
  data() {
    return {
      title: 'Articles',
      pageSize: 9,
      currentPage: 1,
      articleEditorPage,
      searchQuery: '',
      activeCategoryId: null,
      /* 'newest' = новые первые, 'oldest' = старые первые */
      sortOrder: 'newest',
    };
  },
  methods: {
    removeObserveTarget,
    b(el, mods) {
      return b(el, mods);
    },
    ...mapActions({
      getArticlesAction: 'blogs/getArticles',
      onDeleteArticleAction: 'blogs/onDeleteArticle',
      getCategoriesAction: 'blogs/getCategories',
    }),
    /* Загружаем все статьи одним запросом */
    loadAllArticles() {
      this.getArticlesAction({ limit: 10000, offset: 0 });
    },
    onDeleteArticle(id) {
      this.removeObserveTarget();
      this.onDeleteArticleAction({
        id,
        limit: 10000,
        offset: 0,
      });
    },
    setPagination(page, pageSize) {
      this.currentPage = page;
      this.pageSize = pageSize;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    setActiveCategory(categoryId) {
      this.activeCategoryId = categoryId;
      this.currentPage = 1;
    },
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === 'newest' ? 'oldest' : 'newest';
      this.currentPage = 1;
    },
    formatDate(date) {
      return dateFormat(date, 'DD MMM YYYY');
    },
    truncate(text, maxLength = 100) {
      if (!text) return '';
      return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
    },
  },
  mounted() {
    this.loadAllArticles();
    this.getCategoriesAction();
  },
  computed: {
    ...mapState({
      articlesList: (state) => state.blogs.articlesList,
      isArticlesLoaded: (state) => state.blogs.isArticlesLoaded,
      categoriesList: (state) => state.blogs.categoriesList,
    }),
    /* Фильтрация + сортировка по всем статьям */
    filteredArticles() {
      let list = [...(this.articlesList || [])];

      if (this.activeCategoryId !== null) {
        list = list.filter((a) => a.categoryId === this.activeCategoryId);
      }
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        list = list.filter(
          (a) => (a.title || '').toLowerCase().includes(q)
            || (a.author || '').toLowerCase().includes(q),
        );
      }

      /* Сортировка по дате */
      list.sort((a, c) => {
        const dateA = new Date(a.updatedAt || 0).getTime();
        const dateC = new Date(c.updatedAt || 0).getTime();
        return this.sortOrder === 'newest' ? dateC - dateA : dateA - dateC;
      });

      return list;
    },
    filteredTotal() {
      return this.filteredArticles.length;
    },
    /* Клиентская пагинация — вырезаем нужную страницу из отфильтрованного списка */
    paginatedArticles() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredArticles.slice(start, start + this.pageSize);
    },
  },
  watch: {
    /* Сброс страницы при изменении поиска */
    searchQuery() {
      this.currentPage = 1;
    },
  },
};
</script>

<template>
  <div :class="b()">
    <!-- Header -->
    <div :class="b('header')">
      <div :class="b('header-left')">
        <h1 :class="b('title')">Articles</h1>
        <span :class="b('count')">{{ filteredTotal }} total</span>
      </div>
      <a-button
        type="primary"
        size="large"
        :class="b('add-btn')"
        @click="$router.push(articleEditorPage)"
      >
        <PlusOutlined />
        New Article
      </a-button>
    </div>

    <!-- Toolbar: search + sort + category pills -->
    <div :class="b('toolbar')">
      <div :class="b('toolbar-row')">
        <div :class="b('search-wrapper')">
          <a-input
            v-model:value="searchQuery"
            :class="b('search')"
            placeholder="Search by title or author..."
            allow-clear
            size="large"
          >
            <template #prefix>
              <SearchOutlined style="color: var(--Text-Secondary)" />
            </template>
          </a-input>
        </div>

        <a-button :class="b('sort-btn')" @click="toggleSortOrder">
          <SortDescendingOutlined v-if="sortOrder === 'newest'" />
          <SortAscendingOutlined v-else />
          {{ sortOrder === 'newest' ? 'Newest first' : 'Oldest first' }}
        </a-button>
      </div>

      <div :class="b('categories')" v-if="categoriesList && categoriesList.length">
        <button
          :class="b('pill', { active: activeCategoryId === null })"
          @click="setActiveCategory(null)"
        >
          All
        </button>
        <button
          v-for="cat in categoriesList"
          :key="cat.id"
          :class="b('pill', { active: activeCategoryId === cat.id })"
          @click="setActiveCategory(cat.id)"
        >
          {{ cat.name }}
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="!isArticlesLoaded" :class="b('loading')">
      <a-spin size="large" />
      <span>Loading articles...</span>
    </div>

    <!-- Card grid -->
    <div v-else-if="paginatedArticles.length" :class="b('grid')">
      <div
        v-for="article in paginatedArticles"
        :key="article.id"
        :class="b('card')"
      >
        <!-- Image -->
        <div
          :class="b('card-image')"
          role="button"
          tabindex="0"
          @click="$router.push(`${articleEditorPage}/${article.id}`)"
          @keydown.enter="$router.push(`${articleEditorPage}/${article.id}`)"
        >
          <img
            v-if="article.previewImageUri"
            :src="article.previewImageUri"
            :alt="article.title"
            loading="lazy"
          />
          <div v-else :class="b('card-image-placeholder')">
            <FolderOutlined />
          </div>
          <span
            v-if="article.isFeatured"
            :class="b('featured-badge')"
          >
            <StarFilled /> Featured
          </span>
        </div>

        <!-- Body -->
        <div :class="b('card-body')">
          <div :class="b('card-category-tag')" v-if="article.category">
            {{ article.category }}
          </div>
          <h3
            :class="b('card-title')"
            role="button"
            tabindex="0"
            @click="$router.push(`${articleEditorPage}/${article.id}`)"
            @keydown.enter="$router.push(`${articleEditorPage}/${article.id}`)"
          >
            {{ article.title }}
          </h3>
          <p :class="b('card-description')">
            {{ truncate(article.descriptionSeo, 120) }}
          </p>
          <div :class="b('card-meta')">
            <span :class="b('card-meta-item')">
              <UserOutlined />
              {{ article.author || 'Unknown' }}
            </span>
            <span :class="b('card-meta-item')">
              <CalendarOutlined />
              {{ formatDate(article.updatedAt) }}
            </span>
          </div>
        </div>

        <!-- Actions -->
        <div :class="b('card-actions')">
          <a-button
            type="text"
            :class="b('action-edit')"
            @click="$router.push(`${articleEditorPage}/${article.id}`)"
          >
            <EditOutlined /> Edit
          </a-button>
          <a-popconfirm
            title="Are you sure you want to delete this article?"
            ok-text="Delete"
            cancel-text="Cancel"
            ok-type="danger"
            @confirm="onDeleteArticle(article.id)"
          >
            <a-button
              type="text"
              danger
              :class="b('action-delete')"
            >
              <DeleteOutlined /> Delete
            </a-button>
          </a-popconfirm>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else :class="b('empty')">
      <a-empty description="No articles found">
        <template #image>
          <div :class="b('empty-icon')">
            <FolderOutlined />
          </div>
        </template>
        <a-button
          v-if="searchQuery || activeCategoryId !== null"
          type="link"
          @click="searchQuery = ''; activeCategoryId = null"
        >
          Clear filters
        </a-button>
      </a-empty>
    </div>

    <!-- Pagination -->
    <div :class="b('pagination')" v-if="isArticlesLoaded && filteredTotal > pageSize">
      <a-pagination
        :current="currentPage"
        :page-size="pageSize"
        :total="filteredTotal"
        :show-size-changer="true"
        :page-size-options="['9', '18', '36']"
        @change="setPagination"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/media';

.articles-page {
  padding: 32px;
  max-width: 1440px;
  margin: 0 auto;

  @include media($small) {
    padding: 20px 16px;
  }

  // ── Header ──
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 28px;

    @include media($xsmall) {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }
  }

  &__header-left {
    display: flex;
    align-items: baseline;
    gap: 12px;
  }

  &__title {
    font-size: 28px;
    font-weight: 700;
    color: var(--Text-Primary);
    margin: 0;
    letter-spacing: -0.5px;
  }

  &__count {
    font-size: 14px;
    color: var(--Text-Secondary);
    background: var(--Background-Tertiary);
    padding: 3px 10px;
    border-radius: 12px;
    font-weight: 500;
  }

  &__add-btn {
    border-radius: 8px;
    font-weight: 600;
    height: 42px;
    padding: 0 24px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  // ── Toolbar ──
  &__toolbar {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 28px;
    padding-bottom: 24px;
    border-bottom: 1px solid var(--Border-Color);
  }

  &__toolbar-row {
    display: flex;
    align-items: center;
    gap: 12px;

    @include media($xsmall) {
      flex-direction: column;
      align-items: stretch;
    }
  }

  &__search-wrapper {
    flex: 1;
    max-width: 400px;

    @include media($xsmall) {
      max-width: 100%;
    }
  }

  &__sort-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    border-radius: 8px;
    font-weight: 500;
    font-size: 13px;
    color: var(--Text-Secondary);
    border-color: var(--Border-Color);
    background: var(--Background-Secondary);
    height: 40px;
    padding: 0 16px;
    white-space: nowrap;
    transition: all 0.2s ease;

    &:hover {
      border-color: var(--Button-Primary);
      color: var(--Button-Primary);
    }
  }

  &__search {
    border-radius: 10px;

    :deep(.ant-input) {
      background: var(--Background-Secondary);
    }

    :deep(.ant-input-affix-wrapper) {
      background: var(--Background-Secondary);
      border-color: var(--Border-Color);
      border-radius: 10px;
      padding: 8px 14px;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        border-color: var(--Button-Primary);
      }

      &:focus {
        border-color: var(--Button-Primary);
        box-shadow: 0 0 0 3px rgba(91, 143, 163, 0.12);
      }
    }

    :deep(.ant-input-affix-wrapper-focused) {
      border-color: var(--Button-Primary);
      box-shadow: 0 0 0 3px rgba(91, 143, 163, 0.12);
    }
  }

  // ── Category Pills ──
  &__categories {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__pill {
    padding: 6px 18px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    background: var(--Background-Secondary);
    color: var(--Text-Secondary);
    border: 1px solid var(--Border-Color);
    transition: all 0.2s ease;
    white-space: nowrap;
    outline: none;

    &:hover {
      border-color: var(--Button-Primary);
      color: var(--Button-Primary);
      background: var(--Background-Main);
    }

    &_active {
      background: var(--Button-Primary);
      color: var(--Text-Inverse);
      border-color: var(--Button-Primary);

      &:hover {
        background: var(--Button-Primary-Hover);
        border-color: var(--Button-Primary-Hover);
        color: var(--Text-Inverse);
      }
    }
  }

  // ── Loading ──
  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 80px 0;
    color: var(--Text-Secondary);
    font-size: 14px;
  }

  // ── Card Grid ──
  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;

    @include media($small) {
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
    }

    @include media($xsmall) {
      grid-template-columns: 1fr;
      gap: 16px;
    }
  }

  // ── Card ──
  &__card {
    background: var(--Background-Main);
    border: 1px solid var(--Border-Color);
    border-radius: 14px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease;

    &:hover {
      box-shadow: var(--Shadow-Medium);
      transform: translateY(-3px);
      border-color: var(--Border-Dark);
    }
  }

  &__card-image {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 10;
    overflow: hidden;
    background: var(--Background-Tertiary);
    cursor: pointer;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.4s ease;
    }

    &:hover img {
      transform: scale(1.04);
    }
  }

  &__card-image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 40px;
    color: var(--Border-Dark);
  }

  &__featured-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    background: var(--Button-Accent);
    color: var(--Dark);
    display: flex;
    align-items: center;
    gap: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &__card-body {
    padding: 18px 20px 14px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  &__card-category-tag {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: var(--Button-Primary);
    margin-bottom: 8px;
  }

  &__card-title {
    font-size: 17px;
    font-weight: 600;
    color: var(--Text-Primary);
    margin: 0 0 10px;
    line-height: 1.35;
    cursor: pointer;
    transition: color 0.2s ease;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;

    &:hover {
      color: var(--Button-Primary);
    }
  }

  &__card-description {
    font-size: 13px;
    color: var(--Text-Secondary);
    line-height: 1.55;
    margin: 0 0 auto;
    padding-bottom: 12px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__card-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    padding-top: 12px;
    border-top: 1px solid var(--Border-Light);
  }

  &__card-meta-item {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: var(--Text-Secondary);
  }

  // ── Card Actions ──
  &__card-actions {
    display: flex;
    justify-content: flex-end;
    gap: 4px;
    padding: 10px 16px;
    border-top: 1px solid var(--Border-Light);
    background: var(--Background-Secondary);
  }

  &__action-edit {
    color: var(--Button-Primary);
    font-weight: 500;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 4px;

    &:hover {
      color: var(--Button-Primary-Hover);
      background: transparent;
    }
  }

  &__action-delete {
    font-weight: 500;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  // ── Empty State ──
  &__empty {
    padding: 80px 0;
    text-align: center;
  }

  &__empty-icon {
    font-size: 48px;
    color: var(--Border-Dark);
    margin-bottom: 8px;
  }

  // ── Pagination ──
  &__pagination {
    display: flex;
    justify-content: center;
    margin-top: 36px;
    padding-top: 24px;
    border-top: 1px solid var(--Border-Light);
  }
}
</style>
